import { youtubeId } from "@baze/schema";
import type { ReactNode } from "react";
import type { RenderContext, SectionProps } from "../context";
import { domId, field } from "../context";
import { Icon } from "../icons";
import { BUTTON, CARD_HOVER, Container, Img, MultiLine, STACK, STAGGER, SectionHeader, SectionShell, SiteLink, cx } from "../ui";

type Content = SectionProps<"lessons">["node"]["content"];
type Group = Content["groups"][number];
type Topic = Group["topics"][number];

export interface LessonEntry {
  topic: Topic;
  group: Group;
  gi: number;
  ti: number;
  /** Global position, 1-based. */
  n: number;
  /** DOM id of the topic's detail view (also its shareable #link). */
  id: string;
}

/** Group card tints, cycling through the design's own colours. */
const TINTS = ["bg-site-primary/10", "bg-site-secondary/15", "bg-site-accent/15", "bg-site-text/5"];

const slug = (s: string) =>
  s
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

/**
 * Topics in reading order, each with a stable id derived from the anchor and its title (≤64 chars,
 * the in-page href limit). Duplicate titles get -2, -3…
 */
export function lessonEntries(anchor: string, c: Content): LessonEntry[] {
  const seen = new Map<string, number>();
  const out: LessonEntry[] = [];
  c.groups.forEach((group, gi) =>
    group.topics.forEach((topic, ti) => {
      const base = `${anchor}-${slug(topic.title) || "materi"}`.slice(0, 58).replace(/-+$/, "");
      const k = (seen.get(base) ?? 0) + 1;
      seen.set(base, k);
      out.push({ topic, group, gi, ti, n: out.length + 1, id: k > 1 ? `${base}-${k}` : base });
    }),
  );
  return out;
}

const pad = (n: number) => String(n).padStart(2, "0");

/** In-page link. Inert in the editor, like every other engine link. */
function Hash({ ctx, to, className, children, group }: { ctx: RenderContext; to: string; className?: string; children: ReactNode; group?: number }) {
  return (
    <a href={`#${to}`} className={className} {...(group !== undefined ? { "data-baze-lesson-group": String(group) } : {})} {...(ctx.editor ? { "data-baze-inert": "" } : {})}>
      {children}
    </a>
  );
}

function Detail({ ctx, c, e, next, anchor }: { ctx: RenderContext; c: Content; e: LessonEntry; next?: LessonEntry; anchor: string }) {
  const t = e.topic;
  const p = `content.groups.${e.gi}.topics.${e.ti}`;
  const vid = youtubeId(t.video);
  const titleId = domId(ctx, `${e.id}-judul`);
  return (
    <article id={domId(ctx, e.id)} data-baze-lesson-detail="" aria-labelledby={titleId} className="baze-lesson-detail mx-auto max-w-3xl">
      <Hash ctx={ctx} to={domId(ctx, anchor)} className="inline-flex min-h-10 items-center gap-1 text-sm font-semibold text-site-text hover:text-site-primary">
        <Icon name="chevron-left" className="size-4" />
        {c.backLabel || "Kembali ke daftar materi"}
      </Hash>
      <p className="mt-4">
        <span className="inline-block rounded-site-sm bg-site-primary/10 px-3 py-1 text-xs font-semibold tracking-wide text-site-primary uppercase">{e.group.label || e.group.title}</span>
      </p>
      <h3 id={titleId} className="baze-heading mt-4 text-3xl leading-tight @3xl:text-4xl" {...field(ctx, `${p}.title`)}>
        {t.title}
      </h3>
      {e.group.label ? <p className="mt-2 font-semibold text-site-muted">{e.group.title}</p> : null}
      {t.summary ? (
        <div className="mt-6 rounded-site-md bg-site-primary/10 p-5 @3xl:p-6">
          <p className="text-xs font-bold tracking-[0.12em] text-site-text/80 uppercase">Ringkasan materi</p>
          <MultiLine ctx={ctx} text={t.summary} path={`${p}.summary`} className="mt-2 text-sm leading-relaxed text-site-text @3xl:text-base" />
        </div>
      ) : null}
      {t.image ? (
        <div className="mt-6 aspect-[16/9] overflow-hidden rounded-site-md bg-site-surface">
          <Img ctx={ctx} image={t.image} sizes="(min-width: 768px) 720px, 100vw" path={`${p}.image`} />
        </div>
      ) : null}
      {t.points?.length ? (
        <ul className="mt-6 space-y-3">
          {t.points.map((pt, i) => (
            <li key={i} className="flex gap-3 text-site-text">
              <Icon name="check" className="mt-0.5 size-5 shrink-0 text-site-primary" />
              <span {...field(ctx, `${p}.points.${i}`)}>{pt}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {vid ? (
        <div className="mt-8 aspect-video overflow-hidden rounded-site-md bg-site-text/10 shadow-site-sm">
          {ctx.thumbnail ? null : (
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${vid}`}
              title={`Video: ${t.title}`}
              loading="lazy"
              allow="accelerometer; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              referrerPolicy="strict-origin-when-cross-origin"
              className="h-full w-full border-0"
            />
          )}
        </div>
      ) : null}
      <MultiLine ctx={ctx} text={t.body} path={`${p}.body`} className="mt-8 leading-relaxed text-site-text @3xl:text-lg" />
      {t.pdf || (vid && t.video) ? (
        <div className="mt-8 flex flex-wrap gap-3">
          {t.pdf ? (
            <SiteLink ctx={ctx} href={t.pdf} trackLink fallback="none" className={cx("baze-btn", BUTTON.primary)}>
              <Icon name="download" className="size-[1.125rem] shrink-0" />
              <span>{c.pdfLabel || "Buka PDF"}</span>
            </SiteLink>
          ) : null}
          {vid && t.video ? (
            <SiteLink ctx={ctx} href={t.video} trackLink fallback="none" className={cx("baze-btn", BUTTON.secondary)}>
              <Icon name="youtube" className="size-[1.125rem] shrink-0" />
              <span>{c.videoLabel || "Tonton di YouTube"}</span>
            </SiteLink>
          ) : null}
        </div>
      ) : null}
      {c.note ? (
        <aside className="mt-8 rounded-site-md bg-site-accent/15 p-5">
          {c.note.title ? (
            <p className="font-semibold text-site-text" {...field(ctx, "content.note.title")}>
              {c.note.title}
            </p>
          ) : null}
          <MultiLine ctx={ctx} text={c.note.body} path="content.note.body" className="mt-1 text-sm leading-relaxed text-site-text/85" />
        </aside>
      ) : null}
      {next ? (
        <Hash ctx={ctx} to={domId(ctx, next.id)} className={cx("baze-btn mt-8 w-full justify-between", BUTTON.secondary)}>
          <span className="min-w-0 truncate">
            <span className="text-site-muted">Berikutnya: </span>
            {next.topic.title}
          </span>
          <Icon name="arrow-right" className="size-4 shrink-0" />
        </Hash>
      ) : null}
    </article>
  );
}

/**
 * Learning material. The list and every topic's detail view are in the HTML; CSS `:target` shows one
 * detail in place of the list, so each topic has its own shareable #link and works without JS. The
 * lessons runtime script only adds search and group filtering on top.
 */
export function Lessons({ node, ctx }: SectionProps<"lessons">) {
  const c = node.content;
  const cards = node.variant === "cards";
  const entries = lessonEntries(node.anchor, c);
  const listId = domId(ctx, `${node.anchor}-daftar`);
  const showSearch = c.showSearch !== false;
  const showFilter = c.showFilter !== false && c.groups.length > 1;
  return (
    <SectionShell ctx={ctx} node={node} className="baze-lessons">
      <Container>
        <div data-baze-lesson-list="">
          <SectionHeader ctx={ctx} node={node} eyebrow={c.eyebrow} title={c.title} description={c.description} />
          {cards && c.groups.length > 1 ? (
            <ul className={cx(STACK, STAGGER, "grid grid-cols-2 gap-3 @4xl:grid-cols-4 @3xl:gap-5")}>
              {c.groups.map((g, gi) => (
                <li key={gi}>
                  <Hash ctx={ctx} to={listId} group={gi} className={cx("flex h-full flex-col rounded-site-md p-4 @3xl:p-5", TINTS[gi % TINTS.length], CARD_HOVER)}>
                    {g.label ? (
                      <span className="text-xs font-semibold text-site-muted" {...field(ctx, `content.groups.${gi}.label`)}>
                        {g.label}
                      </span>
                    ) : null}
                    <span className="baze-heading mt-1 text-base leading-snug @3xl:text-lg" {...field(ctx, `content.groups.${gi}.title`)}>
                      {g.title}
                    </span>
                    <span className="mt-auto pt-3 text-xs text-site-muted">{`${g.topics.length} topik`}</span>
                  </Hash>
                </li>
              ))}
            </ul>
          ) : null}
          <div id={listId} className={cx(STACK, "scroll-mt-28")}>
            {showSearch || showFilter ? (
              // Revealed by the runtime script: without JS the controls would do nothing.
              <div data-baze-lesson-ui="" hidden className="mb-5 space-y-3">
                {showSearch ? (
                  <label className="flex min-h-12 items-center gap-2 rounded-site-md border border-site-border bg-site-surface px-4 focus-within:border-site-primary">
                    <span className="sr-only">Cari materi</span>
                    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="size-4 shrink-0 text-site-muted">
                      <circle cx="11" cy="11" r="7" />
                      <path d="m20 20-3.5-3.5" />
                    </svg>
                    <input type="search" data-baze-lesson-search="" placeholder={c.searchPlaceholder || "Cari materi…"} className="min-w-0 flex-1 bg-transparent py-2 text-site-text outline-none placeholder:text-site-muted" />
                  </label>
                ) : null}
                {showFilter ? (
                  <div role="group" aria-label="Saring materi" className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none]">
                    <button type="button" data-baze-lesson-filter="" aria-pressed="true" className="baze-lesson-chip">
                      {`Semua (${entries.length})`}
                    </button>
                    {c.groups.map((g, gi) => (
                      <button key={gi} type="button" data-baze-lesson-filter={String(gi)} aria-pressed="false" className="baze-lesson-chip">
                        {g.label || g.title}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}
            <ol className={cx(STAGGER, "space-y-3")}>
              {entries.map((e) => {
                const p = `content.groups.${e.gi}.topics.${e.ti}`;
                return (
                  <li key={e.id} data-baze-lesson-row="" data-group={String(e.gi)} data-text={`${e.topic.title} ${e.topic.summary ?? ""} ${e.group.title}`.toLowerCase()}>
                    <Hash ctx={ctx} to={domId(ctx, e.id)} className={cx("group flex items-center gap-4 rounded-site-md border border-site-border bg-site-surface p-4 shadow-site-sm", CARD_HOVER)}>
                      <span aria-hidden="true" className="grid size-11 shrink-0 place-items-center rounded-full bg-site-primary/10 text-sm font-bold text-site-primary tabular-nums">
                        {pad(e.n)}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block font-semibold leading-snug text-site-text @3xl:text-lg" {...field(ctx, `${p}.title`)}>
                          {e.topic.title}
                        </span>
                        {e.topic.summary ? <span className="mt-0.5 line-clamp-2 block text-sm text-site-muted">{e.topic.summary}</span> : null}
                        {c.groups.length > 1 ? <span className="mt-2 inline-block rounded-site-sm bg-site-text/5 px-2 py-0.5 text-xs font-semibold text-site-muted">{e.group.label || e.group.title}</span> : null}
                      </span>
                      <Icon name="chevron-right" className="size-5 shrink-0 text-site-muted transition-transform group-hover:translate-x-0.5" />
                    </Hash>
                  </li>
                );
              })}
            </ol>
            <p data-baze-lesson-empty="" hidden className="py-8 text-center text-site-muted">
              Materi tidak ditemukan.
            </p>
          </div>
        </div>
        {entries.map((e, i) => (
          <Detail key={e.id} ctx={ctx} c={c} e={e} next={entries[i + 1]} anchor={node.anchor} />
        ))}
      </Container>
    </SectionShell>
  );
}
